import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { ARTICLES, INSIGHT_TAGS } from "../data/articles";

const EASE = [0.33, 1, 0.68, 1];

const Tag = ({ children }) => (
  <span className="border border-cyber/40 bg-cyber/5 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-cyber">
    {children}
  </span>
);

const ArticleImage = ({ src, alt, className = "" }) => (
  <div className={`relative overflow-hidden border border-white/10 ${className}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-[1.04] group-hover:opacity-100"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/10 to-transparent" />
  </div>
);

const Insights = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const featured = ARTICLES.find((a) => a.featured);
  const showFeatured = filter === "All" || filter === featured.tag;
  const list = useMemo(
    () => ARTICLES.filter((a) => !a.featured && (filter === "All" || a.tag === filter)),
    [filter]
  );

  return (
    <main className="min-h-screen bg-abyss pb-32 pt-36 md:pt-44" data-testid="insights-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-neon md:text-sm"
          data-testid="insights-overline"
        >
          // ECOSYSTEM NEWS
        </motion.p>

        <div className="mt-6 overflow-hidden">
          <motion.h1
            initial={{ y: "60%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 1, ease: EASE }}
            className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tighter sm:text-6xl md:text-7xl"
            data-testid="insights-headline"
          >
            Signals from
            <br />
            <span className="text-outline-cyan">the Factory Floor.</span>
          </motion.h1>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="mt-8 max-w-xl text-sm leading-relaxed text-secondary md:text-base"
          data-testid="insights-subheadline"
        >
          Updates, dispatches, and build reports from all five operating
          divisions of Future Factory Group.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-12 flex flex-wrap gap-3"
          data-testid="insights-filters"
        >
          {INSIGHT_TAGS.map((tag) => (
            <button
              key={tag}
              data-testid={`insights-filter-${tag.toLowerCase().replace(" ", "-")}`}
              onClick={() => setFilter(tag)}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                filter === tag
                  ? "border-neon bg-neon/10 text-neon"
                  : "border-white/15 text-white/50 hover:border-cyber/60 hover:text-cyber"
              }`}
            >
              {tag}
            </button>
          ))}
        </motion.div>

        {showFeatured && (
          <motion.button
            layout
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45, ease: EASE }}
            onClick={() => setActive(featured)}
            className="group mt-16 grid w-full grid-cols-1 overflow-hidden border border-white/10 bg-surface text-left transition-colors duration-500 hover:border-cyber/60 lg:grid-cols-2"
            data-testid="insights-featured-article"
          >
            <ArticleImage src={featured.image} alt={featured.title} className="aspect-video border-0 lg:aspect-auto lg:min-h-[380px]" />
            <div className="flex flex-col justify-between p-8 md:p-12">
              <div>
                <div className="flex items-center gap-4">
                  <Tag>{featured.tag}</Tag>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
                    {featured.date} — {featured.readTime}
                  </span>
                </div>
                <h2 className="mt-8 font-display text-2xl font-medium leading-tight tracking-tight md:text-4xl">
                  {featured.title}
                </h2>
                <p className="mt-6 max-w-lg text-sm leading-relaxed text-secondary md:text-base">
                  {featured.excerpt}
                </p>
              </div>
              <span className="mt-10 flex w-fit items-center gap-2 border-b border-white/20 pb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 group-hover:border-cyber group-hover:text-cyber">
                Read Transmission
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </motion.button>
        )}

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="insights-grid">
          {list.map((article, i) => (
            <motion.button
              key={article.id}
              layout
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }}
              onClick={() => setActive(article)}
              className="group flex flex-col overflow-hidden border border-white/10 bg-surface text-left transition-colors duration-500 hover:border-cyber/60"
              data-testid={`article-card-${article.id}`}
            >
              <ArticleImage src={article.image} alt={article.title} className="aspect-video border-0" />
              <div className="flex flex-1 flex-col p-7">
                <div className="flex items-center gap-3">
                  <Tag>{article.tag}</Tag>
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/35">
                    {article.date}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-lg font-medium leading-snug tracking-tight md:text-xl">
                  {article.title}
                </h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-secondary">{article.excerpt}</p>
                <span className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 transition-colors duration-300 group-hover:text-cyber">
                  {article.readTime} →
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0B0C10]/90 p-4 backdrop-blur-md"
            onClick={() => setActive(null)}
            data-testid="article-overlay-backdrop"
          >
            <motion.article
              initial={{ opacity: 0, y: 48 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 32 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="hud-corners max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-white/15 bg-surface"
              data-testid="article-overlay"
              role="dialog"
              aria-modal="true"
            >
              <div className="relative">
                <img src={active.image} alt={active.title} className="aspect-video w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                <button
                  data-testid="article-overlay-close"
                  onClick={() => setActive(null)}
                  aria-label="Close article"
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-white/20 bg-[#0B0C10]/70 text-white/70 backdrop-blur transition-colors duration-300 hover:border-cyber hover:text-cyber"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-4">
                  <Tag>{active.tag}</Tag>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
                    {active.date} — {active.readTime}
                  </span>
                </div>
                <h2 className="mt-6 font-display text-2xl font-medium leading-tight tracking-tight md:text-4xl" data-testid="article-overlay-title">
                  {active.title}
                </h2>
                <div className="mt-8 space-y-6">
                  {active.body.map((p, i) => (
                    <p key={i} className="text-sm leading-relaxed text-secondary md:text-base">
                      {p}
                    </p>
                  ))}
                </div>
                <p className="mt-10 border-t border-white/10 pt-6 font-mono text-[9px] uppercase tracking-[0.3em] text-white/30">
                  FUTURE FACTORY GROUP // TRANSMISSION LOG
                </p>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default Insights;
